import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { formatDate } from '../../lib/datetime'
import { displayName, initial, useEmployees } from '../../lib/employees'
import type { EmployeeRow } from '../../lib/employees'
import {
  MONTH_TH,
  dayHourMin,
  dowText,
  errorText,
  humanText,
  leaveRange,
  minutesText,
  usePerson,
} from '../../lib/person'
import type { LeaveForm, PersonMonth, PersonYear } from '../../lib/person'
import { Pager } from '../../lib/Pager'
import { SectionHead } from '../offsite/parts'

/**
 * รายงาน "ข้อมูลบุคคล" — ดูทีละคน ทีละปี ส่วนที่เป็นรายเดือนเลือกเดือนได้
 *
 * รวมของที่ระบบ HR เดิมแยกไว้ 4 หน้า:
 *   Home.aspx            → โปรไฟล์ + สรุปสิทธิ์ลา + สถิติการทำงาน 12 เดือน
 *   Leave.aspx?action=1  → ใบลา
 *   Leave.aspx?action=2  → ใบปรับปรุงเวลา (LEAVE_TYPE 999 ไม่ใช่การลา ไม่ตัดโควตา)
 *   TimeAttendance.aspx  → เวลาเข้า–ออกรายวัน (+ OT ซึ่งของเดิมโชว์แค่เรท 1.5/2/3)
 *
 * ⚠️ ข้อมูลทั้งหมดมาจาก backend ของโปรเจกต์นี้ ไม่เรียก API ระบบเดิมเลย
 *
 * ⚠️ สองก้อนโหลดคนละจังหวะโดยตั้งใจ: ก้อน "รายปี" โหลดเมื่อเปลี่ยนคน/ปี ส่วนก้อน
 *    "รายเดือน" โหลดเมื่อเปลี่ยนเดือน — ถ้ารวมเป็นคำขอเดียว การเปลี่ยนเดือน
 *    จะดึงข้อมูลทั้งปีใหม่ทุกครั้งโดยไม่จำเป็น
 */
export default function PersonReport() {
  const person = usePerson()

  const [empCode, setEmpCode] = useState<string | null>(null)
  const [year, setYear] = useState<number | null>(null)
  const [month, setMonth] = useState<string | null>(null)

  const [data, setData] = useState<PersonYear | null>(null)
  const [monthData, setMonthData] = useState<PersonMonth | null>(null)
  const [loading, setLoading] = useState(false)
  const [monthLoading, setMonthLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadYear = useCallback(async () => {
    if (!empCode) return
    setLoading(true)
    try {
      const res = await person.year(empCode, year ?? undefined)
      setData(res)
      setYear(res.year)
      setError(null)
    } catch (err) {
      setError(errorText(err))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [person, empCode, year])

  useEffect(() => {
    void loadYear()
  }, [loadYear])

  const loadMonth = useCallback(async () => {
    if (!empCode) return
    setMonthLoading(true)
    try {
      const res = await person.month(empCode, month ?? undefined)
      setMonthData(res)
      setMonth(res.month)
    } catch (err) {
      setError(errorText(err))
      setMonthData(null)
    } finally {
      setMonthLoading(false)
    }
  }, [person, empCode, month])

  useEffect(() => {
    void loadMonth()
  }, [loadMonth])

  /**
   * กด "ดูเวลาเข้า–ออก" ที่ตารางรายเดือน แล้วเลื่อนไปให้เห็นผลด้วย
   *
   * ⚠️ ถ้าไม่เลื่อนให้ ปุ่มจะกลายเป็นปุ่มที่ "กดแล้วไม่มีอะไรเกิดขึ้น" — ตารางที่มัน
   *    เปลี่ยนอยู่ล่างสุดของหน้า พ้นจอไปไกล ผู้ใช้จึงไม่รู้ว่าปุ่มทำอะไร
   */
  const dailyRef = useRef<HTMLDivElement>(null)
  function showMonth(m: string) {
    setMonth(m)
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    dailyRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
  }

  // เปลี่ยนคน = ล้างของเดิมทิ้งทั้งหมด ไม่งั้นจะเห็นข้อมูลคนก่อนหน้าค้างอยู่
  // ระหว่างรอโหลด ซึ่งอ่านแล้วเข้าใจผิดว่าเป็นของคนใหม่
  function pick(code: string) {
    setEmpCode(code)
    setYear(null)
    setMonth(null)
    setData(null)
    setMonthData(null)
    setError(null)
  }

  return (
    <>
      <h1>ข้อมูลบุคคล</h1>
      <p className="meta">
        โปรไฟล์ · สิทธิ์การลา · ใบลา · ใบปรับปรุงเวลา · สถิติการทำงาน และเวลาเข้า–ออกรายวัน
        ของพนักงานทีละคน — อ่านจากระบบ HR โดยตรง
      </p>

      <div className="emp-toolbar">
        <EmployeePicker current={data?.employee ?? null} onPick={pick} />

        {data && (
          <label>
            ปี
            <select
              value={year ?? ''}
              onChange={(e) => setYear(Number(e.target.value))}
              disabled={loading}
            >
              {data.years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      {!empCode && (
        <p className="meta">เลือกพนักงานจากช่องค้นหาด้านบนเพื่อดูข้อมูล</p>
      )}

      {empCode && loading && !data && <p className="meta">กำลังโหลด…</p>}

      {data && (
        <>
          <ProfileCard data={data} />
          <QuotaSection data={data} onReload={() => void loadYear()} loading={loading} />
          <MonthlyStatsSection
            data={data}
            selected={month}
            onSelectMonth={showMonth}
            onReload={() => void loadYear()}
            loading={loading}
          />
          <FormsSection
            title="ใบลา"
            kind="leave"
            year={data.year}
            forms={data.leave.leaves}
            truncated={data.leave.summary.truncated}
            onReload={() => void loadYear()}
            loading={loading}
          />
          <FormsSection
            title="ใบปรับปรุงเวลา"
            kind="adjust"
            year={data.year}
            forms={data.leave.adjustments}
            truncated={data.leave.summary.truncated}
            onReload={() => void loadYear()}
            loading={loading}
          />
          <DailySection
            innerRef={dailyRef}
            monthData={monthData}
            loading={monthLoading}
            onMonth={setMonth}
            onReload={() => void loadMonth()}
          />
        </>
      )}
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────────── */

/** ช่องค้นหาพนักงาน — ค้นจากรายชื่อจริง ไม่ให้พิมพ์รหัสลอย ๆ */
function EmployeePicker({
  current,
  onPick,
}: {
  current: { empCode: string; fullName: string | null } | null
  onPick: (empCode: string) => void
}) {
  const employees = useEmployees()
  const [term, setTerm] = useState('')
  const [list, setList] = useState<EmployeeRow[] | null>(null)
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      employees
        .list({ q: term, status: 'all', pageSize: 15 })
        .then((r) => setList(r.employees))
        .catch(() => setList([]))
    }, 200)
    return () => clearTimeout(id)
  }, [employees, term, open])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div className="combo emp-search" ref={box}>
      <input
        type="search"
        value={open ? term : current ? `${current.empCode} · ${current.fullName ?? ''}` : ''}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => {
          setTerm('')
          setOpen(true)
        }}
        placeholder="ค้นด้วยรหัส หรือชื่อ–สกุล"
        aria-label="เลือกพนักงาน"
      />
      {open && (
        <ul className="combo-list">
          {list === null && <li className="combo-item">กำลังค้น…</li>}
          {list?.length === 0 && <li className="combo-item">ไม่พบพนักงานที่ตรงกับคำค้น</li>}
          {list?.map((e) => (
            <li key={e.empCode}>
              <button
                type="button"
                className={
                  e.empCode === current?.empCode ? 'combo-item combo-item--active' : 'combo-item'
                }
                onClick={() => {
                  onPick(e.empCode)
                  setOpen(false)
                }}
              >
                <span className="emp-avatar" aria-hidden="true">
                  {initial(e)}
                </span>{' '}
                {e.empCode} · {displayName(e)}
                {e.status !== 'active' && <span className="chip chip--off">ไม่ได้ทำงานแล้ว</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * การ์ดโปรไฟล์ — ฟิลด์ชุดเดียวกับส่วนบนของ Home.aspx เดิม
 *
 * ⚠️ ห้ามใช้ .field-grid (ตัวจัดฟอร์ม) — มันเป็น auto-fit ตามความกว้างที่มี
 *    หน้านี้ตั้ง wide ไว้ บนจอ 2000px จึงแตกเป็น 9 คอลัมน์ ป้ายกับค่าหลุดจากกัน
 *    ที่นี่จึงจัดเป็น 3 กลุ่มตายตัว แล้วให้ยุบเป็นคอลัมน์เดียวเมื่อจอแคบ
 */
function ProfileCard({ data }: { data: PersonYear }) {
  const e = data.employee
  // humanText คัดค่าขยะ (GUID ที่ระบบเขียนทับช่องชื่อ) ออกจากการแสดงผล
  // ไม่ได้ลบข้อมูล — หน้า /admin/employees ยังเห็นค่าดิบไว้ให้แก้
  const nameEng = [humanText(e.firstNameEng), humanText(e.lastNameEng)].filter(Boolean).join(' ') || null

  const groups: { title: string; rows: [string, string | null][] }[] = [
    {
      title: 'ข้อมูลติดต่อ',
      rows: [
        ['ชื่อ–สกุล', humanText(e.fullName)],
        ['อังกฤษ', nameEng],
        ['อีเมล', humanText(e.email)],
        ['เบอร์โทร', humanText(e.phoneNo)],
      ],
    },
    {
      title: 'สังกัด',
      rows: [
        // ⚠️ ป้าย "ฝ่าย" ของระบบเดิมแสดงค่าจากตาราง branch (สาขา) ไม่ใช่ฝ่ายจริง
        //    คงค่าเดิมไว้เพื่อเทียบกับของเก่าได้ แต่ตั้งชื่อป้ายตามข้อมูลจริง
        ['สาขา', humanText(e.branchName ?? e.branchCode)],
        ['แผนก', humanText(e.departName ?? e.departCode)],
        ['ตำแหน่ง', humanText(e.position)],
        ['ระดับ', humanText(e.buDesc)],
      ],
    },
    {
      title: 'การจ้างและสายอนุมัติ',
      rows: [
        ['วันเริ่มงาน', e.workStartDate ? formatDate(e.workStartDate) : null],
        ['หัวหน้าทีม', humanText(e.leaveApproveName)],
        ['หัวหน้าแผนก', humanText(e.approve2Name)],
        ['ผู้บังคับบัญชา', humanText(e.approve3Name)],
      ],
    },
  ]

  return (
    <section className="card">
      <div className="profile-head">
        <span className="profile-avatar" aria-hidden="true">
          {initial(e)}
        </span>
        <div>
          <h2>{displayName(e)}</h2>
          <p className="meta">
            {e.empCode} · {e.departName ?? e.departCode ?? 'ไม่ระบุแผนก'}
          </p>
        </div>
        <span
          className={
            e.status === 'active' ? 'chip chip--ok' : e.status === 'resigned' ? 'chip chip--off' : 'chip chip--warn'
          }
        >
          {e.status === 'active' ? 'ทำงาน' : e.status === 'resigned' ? 'ลาออก' : 'ปิดใช้งาน'}
        </span>
      </div>

      <div className="profile-cols">
        {groups.map((g) => (
          <div key={g.title}>
            <p className="fieldset-title">{g.title}</p>
            {g.rows.map(([label, value]) => (
              <p key={label} className="kv">
                <span className="kv-label">{label}</span>
                <span className="kv-value">{value || '—'}</span>
              </p>
            ))}
          </div>
        ))}
      </div>
    </section>
  )
}

function QuotaSection({
  data,
  onReload,
  loading,
}: {
  data: PersonYear
  onReload: () => void
  loading: boolean
}) {
  const [showUnused, setShowUnused] = useState(false)
  const all = data.leave.quota
  const shown = showUnused ? all : all.filter((q) => q.usedThisYear || q.hasQuotaRow)

  return (
    <section className="card">
      <SectionHead
        title={`สิทธิ์การลา ปี ${data.year}`}
        meta={`${all.length} ประเภท · ใช้ไปรวม ${dayHourMin(data.leave.summary.leaveDays)}`}
        onReload={onReload}
        loading={loading}
      />
      <label className="field-note">
        <input
          type="checkbox"
          checked={showUnused}
          onChange={(e) => setShowUnused(e.target.checked)}
        />{' '}
        แสดงประเภทที่ยังไม่ได้ใช้และไม่มีโควตารายคน ({all.length - shown.length} รายการ)
      </label>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>ประเภทการลา</th>
              {/* ไม่ต้องมี "(วัน)" ต่อท้ายแล้ว — หน่วยเขียนกำกับอยู่ในค่าแต่ละช่อง */}
              <th className="num">สิทธิ์</th>
              <th className="num">ใช้ไป</th>
              <th className="num">คงเหลือ</th>
              <th>ที่มาของสิทธิ์</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((q) => (
              <tr key={q.leaveType}>
                <td>
                  {q.desc}
                  {q.missingFromMaster && (
                    <span className="chip chip--warn">ประเภทนี้ถูกลบจากระบบแล้ว</span>
                  )}
                </td>
                {/* ⚠️ คิดจาก hoursUsed ตัวเดียว ไม่ใช้ dayUsed/dayRemain ที่ backend
                    ปัดมาแล้วทั้งคู่ — ถ้าใช้ค่าที่ปัดแยกกัน "ใช้ไป + คงเหลือ" จะไม่เท่ากับ
                    "สิทธิ์" เพี้ยนไปหลักนาที ซึ่งเป็นสิ่งแรกที่คนอ่านเอานิ้วไล่บวก */}
                <td className="num">{dayHourMin(q.entitlementDays)}</td>
                <td className="num">{dayHourMin(q.hoursUsed / 8)}</td>
                <td className="num">
                  {q.entitlementDays === null ? '—' : dayHourMin(q.entitlementDays - q.hoursUsed / 8)}
                </td>
                <td>
                  {/* ผู้ใช้ต้องแยกออกว่าเลขสิทธิ์เป็นของคนนี้จริง หรือเป็นค่ากลางของประเภท
                      — ของเดิมโชว์ปนกันโดยไม่บอก */}
                  {q.entitlementFrom === 'HR_CAN_LEAVE' ? (
                    <span className="chip chip--ok">โควตารายคน</span>
                  ) : q.entitlementFrom === 'HR_LEAVE_TYPE' ? (
                    <span className="chip">ค่ากลางของประเภท</span>
                  ) : (
                    <span className="chip chip--off">ไม่กำหนดเพดาน</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function MonthlyStatsSection({
  data,
  selected,
  onSelectMonth,
  onReload,
  loading,
}: {
  data: PersonYear
  selected: string | null
  onSelectMonth: (m: string) => void
  onReload: () => void
  loading: boolean
}) {
  const s = data.work.summary
  return (
    <section className="card">
      <SectionHead
        title={`สถิติการทำงาน ปี ${data.year}`}
        meta={`ทำงาน ${s.workDays} วัน · สาย ${s.lateDays} ครั้ง (${minutesText(s.lateMinutes)}) · ขาด ${s.absentDays} วัน`}
        onReload={onReload}
        loading={loading}
      />
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>เดือน</th>
              <th className="num">วันทำงาน</th>
              <th className="num">วันหยุด</th>
              <th className="num">มาสาย</th>
              <th className="num">รวมสาย</th>
              <th className="num">ขาดงาน</th>
              <th className="num">ลา</th>
              <th className="num">ไม่ครบเวลา</th>
              {/* หัวคอลัมน์ต้องมี — คอลัมน์ที่มีแต่ปุ่มโดยไม่บอกว่าปุ่มพาไปไหน
                  คือที่มาของคำถาม "ปุ่มนี้คืออะไร" */}
              <th>รายวัน</th>
            </tr>
          </thead>
          <tbody>
            {data.work.months.map((m) => {
              const q = m.summary
              return (
                <tr key={m.month} className={m.month === selected ? 'emp-row--selected' : undefined}>
                  <td>{MONTH_TH[m.monthNo - 1]}</td>
                  {/* เดือนที่ยังมาไม่ถึงต้องไม่โชว์เลข 0 — 0 อ่านว่า "ไม่มาทำงานเลย" */}
                  {m.isFuture ? (
                    <td colSpan={7} className="meta">
                      ยังไม่ถึงเดือนนี้
                    </td>
                  ) : !m.hasData ? (
                    <td colSpan={7} className="meta">
                      ไม่มีข้อมูลในระบบ
                    </td>
                  ) : (
                    <>
                      <td className="num">{q.workDays}</td>
                      <td className="num">{q.holidays}</td>
                      <td className="num">{q.lateDays || '—'}</td>
                      <td className="num">{q.lateMinutes ? minutesText(q.lateMinutes) : '—'}</td>
                      <td className="num">{q.absentDays || '—'}</td>
                      <td className="num">{q.leaveDays || '—'}</td>
                      <td className="num">{q.missingDays || '—'}</td>
                    </>
                  )}
                  <td>
                    {!m.isFuture && m.hasData && (
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => onSelectMonth(m.month)}
                        // ชื่อเดือนอยู่ในปุ่มไม่ได้ (คอลัมน์แคบ) แต่ต้องอยู่ในชื่อที่
                        // screen reader อ่าน ไม่งั้นได้ยิน "ดูเวลาเข้า-ออก" 8 ปุ่มติดกัน
                        aria-label={`ดูเวลาเข้า–ออกรายวัน เดือน${MONTH_TH[m.monthNo - 1]}`}
                      >
                        {m.month === selected ? 'กำลังดูอยู่' : 'ดูเวลาเข้า–ออก'}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function FormsSection({
  title,
  kind,
  year,
  forms,
  truncated,
  onReload,
  loading,
}: {
  title: string
  /**
   * ใบลา กับ ใบปรับปรุงเวลา ใช้ตารางเดียวกันแต่ตีความคนละแบบ
   *
   * ⚠️ ใบปรับปรุงเวลา (LEAVE_TYPE 999) **ไม่ตัดสิทธิ์ลา** และค่า `hours` ของมัน
   *    คือความกว้างของช่วงเวลาที่ขอแก้ ไม่ใช่จำนวนวันลา — เอามาโชว์เป็น "จำนวน"
   *    แล้วอ่านเป็น "ลาไป 1 วัน 2 ชม." ซึ่งไม่จริงเลย จึงซ่อนคอลัมน์นั้นไปเลย
   */
  kind: 'leave' | 'adjust'
  /** ปีที่กำลังดู — ส่งให้ leaveRange ละปีทิ้งจากวันที่ในแถว */
  year: number
  forms: LeaveForm[]
  /** backend ตัดที่ 600 ใบต่อปี — ถ้าชนเพดานต้องบอก ไม่งั้นตัวแบ่งหน้าจะดูเหมือนครบ */
  truncated: boolean
  onReload: () => void
  loading: boolean
}) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // เปลี่ยนคน/เปลี่ยนปีแล้วต้องกลับหน้า 1 — ไม่งั้นค้างอยู่หน้า 5 ของคนก่อนหน้า
  // แล้วเห็นตารางว่างทั้งที่มีข้อมูล (กับดักเดียวกับตัวกรองในหน้าพนักงาน)
  useEffect(() => {
    setPage(1)
  }, [forms])

  const isLeave = kind === 'leave'
  const counted = forms.filter((f) => f.counted)
  const hours = counted.reduce((s, f) => s + (f.hours ?? 0), 0)

  // ยอดสรุปต้องนับจาก "ทั้งหมด" ไม่ใช่เฉพาะหน้าที่เห็นอยู่
  const shown = forms.slice((page - 1) * pageSize, page * pageSize)

  return (
    <section className="card">
      <SectionHead
        // ปีต้องอยู่ในหัวข้อ เพราะแถวข้างล่างละปีทิ้งไปแล้ว
        title={`${title} ปี ${year}`}
        meta={
          isLeave
            ? `${forms.length} ใบ · นับเป็นสิทธิ์ ${counted.length} ใบ (${dayHourMin(hours / 8)})`
            : // ห้ามเขียน "นับเป็นสิทธิ์" กับใบปรับปรุงเวลา — มันไม่ตัดโควตาลาสักนิด
              `${forms.length} ใบ · ไม่ตัดสิทธิ์การลา`
        }
        onReload={onReload}
        loading={loading}
      />
      {truncated && (
        <p className="warn">
          ปีนี้มีเอกสารเกิน 600 ใบ — ระบบดึงมาแค่ 600 ใบแรก ตัวเลขสรุปข้างบนจึงยังไม่ครบ
        </p>
      )}
      {forms.length === 0 ? (
        <p className="meta">ไม่มีรายการในปีนี้</p>
      ) : (
        <>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="nowrap">ตั้งแต่</th>
                <th className="nowrap">ถึง</th>
                <th>ประเภท</th>
                {isLeave && <th className="num">จำนวน</th>}
                <th>เหตุผล</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((f) => {
                const range = leaveRange(f)
                return (
                <tr key={f.recId}>
                  {/* รูปแบบ YYYY-MM-DD HH:mm ตามกติกาของ lib/datetime.ts
                      ใบที่แก้เวลาด้านเดียวจะเห็นเองว่าฝั่งไหนเป็น '-' */}
                  <td className="nowrap">{range.from}</td>
                  <td className="nowrap">
                    {range.to}
                    {/* ติดป้ายเฉพาะใบข้ามวัน — ช่วยให้สะดุดตาว่าใบนี้ไม่ใช่วันเดียว
                        ทั้งที่สองคอลัมน์ขึ้นต้นด้วยเลขปีเหมือนกันหมด */}
                    {range.multiDay && <span className="chip">ข้ามวัน</span>}
                  </td>
                  <td>{f.desc}</td>
                  {/* จำนวน "วันทำงาน" ไม่ใช่จำนวนวันในช่วง — ของจริงมีใบที่กิน
                      4 วันปฏิทินแต่นับเป็น 2 วัน เพราะวันหยุดคั่น (hours 16 = 2 วัน)
                      ถ้าไม่โชว์ คนอ่านจะนับเองจากช่วงวันแล้วได้เลขผิด
                      ใบปรับปรุงเวลาไม่มีคอลัมน์นี้ — ดูเหตุผลที่ prop `kind` */}
                  {isLeave && (
                    <td className="num" title={f.hours === null ? undefined : `${f.hours} ชั่วโมง`}>
                      {f.hours === null || f.hours === 0 ? '—' : dayHourMin(f.hours / 8)}
                    </td>
                  )}
                  {/* เหตุผลบางใบยาวเป็นย่อหน้า ถ้าปล่อยจะดันคอลัมน์อื่นจนตารางเละ
                      — ตัดด้วย CSS แล้วเก็บฉบับเต็มไว้ใน title */}
                  <td className="cell-clip" title={f.reason ?? undefined}>
                    {f.reason || '—'}
                  </td>
                  <td>
                    <span className={`chip chip--${f.statusTone}`}>{f.statusText}</span>
                    {!f.counted && f.status !== 'CANCEL' && f.status !== 'REJECT' && (
                      <span className="chip">ยังไม่ตัดสิทธิ์</span>
                    )}
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <Pager
          page={page}
          pageSize={pageSize}
          total={forms.length}
          loading={loading}
          onPage={setPage}
          onPageSize={(size) => {
            setPageSize(size)
            setPage(1)
          }}
          /* ต้องมี 10 ในรายการ เพราะเป็นค่าตั้งต้นของส่วนนี้ — ค่าเริ่มต้นของ Pager
             คือ [25,50,100] ซึ่งจะทำให้ช่อง "ต่อหน้า" ว่างเปล่า */
          sizes={[10, 25, 50, 100]}
          unit="ใบ"
        />
        </>
      )}
    </section>
  )
}

function DailySection({
  innerRef,
  monthData,
  loading,
  onMonth,
  onReload,
}: {
  /** ปุ่มในตารางรายเดือนเลื่อนมาที่นี่ — ดูเหตุผลที่ showMonth() */
  innerRef: React.RefObject<HTMLDivElement | null>
  monthData: PersonMonth | null
  loading: boolean
  onMonth: (m: string) => void
  onReload: () => void
}) {
  /* OT รายวันแยกตามเรต (hr มีคอลัมน์ OT 1.5 / 2.0 / 3.0 แยกกัน)
     ใช้ it.rates[] ที่ backend ส่งมา (แยกนาทีต่อเรตในใบ) · ใบ estimated ที่ไม่มี
     rates ให้ลงตาม it.rate เดียว (ถ้ามี) — เรตอื่นที่ไม่ใช่ 1.5/2/3 รวมเข้า 'other' */
  const otByDate = useMemo(() => {
    const map = new Map<string, { r15: number; r2: number; r3: number; other: number }>()
    const add = (date: string, rate: number | null, minutes: number) => {
      if (!minutes) return
      const cur = map.get(date) ?? { r15: 0, r2: 0, r3: 0, other: 0 }
      if (rate === 1.5) cur.r15 += minutes
      else if (rate === 2) cur.r2 += minutes
      else if (rate === 3) cur.r3 += minutes
      else cur.other += minutes
      map.set(date, cur)
    }
    for (const o of monthData?.ot.items ?? []) {
      if (o.rates.length > 0) for (const r of o.rates) add(o.otDate, r.rate, r.minutes)
      else add(o.otDate, o.rate, o.minutes) // estimated / ใบไม่มี detail
    }
    return map
  }, [monthData])

  if (!monthData) {
    return (
      <section className="card" ref={innerRef}>
        <h2>เวลาเข้า–ออกรายวัน</h2>
        <p className="meta">{loading ? 'กำลังโหลด…' : 'ยังไม่มีข้อมูล'}</p>
      </section>
    )
  }

  const s = monthData.work.summary
  const monthLabel = `${MONTH_TH[Number(monthData.month.slice(5, 7)) - 1]} ${monthData.month.slice(0, 4)}`

  return (
    <section className="card" ref={innerRef}>
      <SectionHead
        // เดือนต้องอยู่ในหัวข้อ ไม่ใช่ซ่อนอยู่ใน dropdown อย่างเดียว —
        // คนที่เลื่อนมาจากตารางรายเดือนต้องเห็นทันทีว่ามาถูกเดือนไหม
        title={`เวลาเข้า–ออกรายวัน · ${monthLabel}`}
        meta={`ครบเวลา ${s.completeDays}/${s.workDays} วัน · สาย ${s.lateDays} · ขาด ${s.absentDays} · ปรับปรุงเวลา ${s.adjustedDays}`}
        onReload={onReload}
        loading={loading}
      />

      <label>
        เดือน
        <select
          value={monthData.month}
          onChange={(e) => onMonth(e.target.value)}
          disabled={loading}
        >
          {monthData.monthsAvailable.map((m) => (
            <option key={m} value={m}>
              {MONTH_TH[Number(m.slice(5, 7)) - 1]} {m.slice(0, 4)}
            </option>
          ))}
        </select>
      </label>

      {monthData.work.days.length === 0 ? (
        <p className="meta">
          {monthData.isFuture ? 'เดือนนี้ยังมาไม่ถึง' : 'ไม่มีข้อมูลการทำงานในเดือนนี้'}
        </p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            {/* 16 คอลัมน์เหมือน hr.simat.co.th (กะ = เพิ่มจาก hr เพราะมีประโยชน์) */}
            <thead>
              <tr>
                <th>วันที่</th>
                <th>กะ</th>
                <th className="nowrap">เริ่มงาน</th>
                <th className="nowrap">ออกงาน</th>
                <th className="nowrap">เข้างาน</th>
                <th className="nowrap">เลิกงาน</th>
                <th className="num">สาย</th>
                <th className="nowrap">ปรับปรุงเข้า</th>
                <th className="nowrap">ปรับปรุงเลิก</th>
                <th>การลา</th>
                <th className="nowrap">เริ่มลา</th>
                <th className="nowrap">สิ้นสุดลา</th>
                <th className="num">OT 1.5</th>
                <th className="num">OT 2.0</th>
                <th className="num">OT 3.0</th>
                <th>หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              {monthData.work.days.map((d) => {
                const ot = otByDate.get(d.workDate) ?? { r15: 0, r2: 0, r3: 0, other: 0 }
                return (
                  <tr key={d.workDate}>
                    <td className="nowrap">
                      {formatDate(d.workDate)} {dowText(d.dow)}
                    </td>
                    {/* ⚠️ วันลาต้องมาก่อน noTimeRequired
                        คนกลุ่ม "ไม่ต้องบันทึกเวลา" ลาได้ และ workDayRemark ของวันนั้น
                        ก็เป็น 'ไม่ต้องบันทึกเวลา' เหมือนกัน → ถ้าเช็ค noTimeRequired ก่อน
                        วันลาจะหายไปทั้งคอลัมน์กะและคอลัมน์หมายเหตุ แล้วสถิติบอกว่าลา 5 วัน
                        แต่หาไม่เจอว่าวันไหน (เคสจริง: 0920 พ.ค. 2026) */}
                    <td>
                      {d.isHoliday ? (
                        <span className="chip chip--off">วันหยุด</span>
                      ) : d.onLeave ? (
                        <span className="chip chip--warn">{d.leaveTypeDesc ?? 'ลา'}</span>
                      ) : d.noTimeRequired ? (
                        <span className="chip">ไม่ต้องบันทึกเวลา</span>
                      ) : (
                        (d.shiftName ?? '—')
                      )}
                    </td>
                    <td className="nowrap">{d.shiftStart ?? '—'}</td>
                    <td className="nowrap">{d.shiftEnd ?? '—'}</td>
                    {/* ⚠️ คอลัมน์นี้คือ "เวลาที่ตอกจริง" ต้องเป็น punchIn/punchOut
                        ห้ามใช้ inTime/outTime — นั่นคือเวลาหลัง HR แก้ ซึ่งใช้คิดเงินเดือน
                        แต่ไม่ใช่สิ่งที่เครื่องสแกนบันทึก (เคสจริง 1416 2026-07-19:
                        ตอก 10:02 แต่ inTime เป็น 08:30 เพราะมีใบปรับปรุงเวลาอนุมัติ)
                        ถ้าสองค่าต่างกัน ติดป้ายบอกว่านับเป็นเท่าไร จะได้เห็นทั้งสองฝั่ง */}
                    <td className="nowrap">
                      {d.punchIn ?? '—'}
                      {/* ใช้ข้อความจาง ไม่ใช่ป้ายสี — 26% ของวันทำงานเข้าเงื่อนไขนี้
                          (ส่วนใหญ่คือไม่ได้ตอกแต่ระบบนับให้) ป้ายสีจะรกทั้งตาราง */}
                      {d.correctedIn && (
                        <span className="cell-note">นับ {d.inTime ?? '—'}</span>
                      )}
                    </td>
                    <td className="nowrap">
                      {d.punchOut ?? '—'}
                      {d.outDayOffset > 0 && <span className="chip">วันถัดไป</span>}
                      {d.correctedOut && (
                        <span className="cell-note">นับ {d.outTime ?? '—'}</span>
                      )}
                    </td>
                    <td className="num">{d.lateMinute ? minutesText(d.lateMinute) : '—'}</td>
                    {/* ปรับปรุงเข้า/เลิก = ใบปรับปรุงเวลา (type 999) = timeEdits
                        ตรงกับ hr (FROM_DATE3/TO_DATE3 จาก HR_EMP_LEAVE 999) — ยืนยันจาก
                        source TimeAttendance2.aspx.cs + DB 1615 ต.ค.2026 */}
                    <td className="nowrap">{d.timeEdits[0]?.from ?? '—'}</td>
                    <td className="nowrap">
                      {d.timeEdits[0]?.to ?? '—'}
                      {d.timeEdits[0]?.toNextDay && <span className="chip">วันถัดไป</span>}
                      {d.timeEdits[0] && !d.timeEdits[0].approved && (
                        <span className="cell-note">รออนุมัติ</span>
                      )}
                    </td>
                    {/* การลา = ชื่อประเภท · เริ่มลา/สิ้นสุดลา = ช่วงของใบแรกในวันนั้น
                        (hr แยก 3 คอลัมน์) · ยังไม่อนุมัติติดสถานะต่อท้ายชื่อ */}
                    <td>
                      {d.onLeave ? (
                        <>
                          {d.leaveTypeDesc ?? 'ลา'}
                          {d.leaves[0] && !d.leaves[0].approved && (
                            <span className="cell-note">{d.leaves[0].statusText}</span>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="nowrap">{d.leaves[0]?.from ?? '—'}</td>
                    <td className="nowrap">{d.leaves[0]?.to ?? '—'}</td>
                    <td className="num">
                      {ot.r15 ? minutesText(ot.r15) : '—'}
                      {/* เรตอื่น (เช่น x1.0) ที่ไม่เข้า 3 ช่อง — ไม่ทิ้ง โชว์จางไว้ */}
                      {ot.other > 0 && <span className="cell-note">+เรตอื่น {minutesText(ot.other)}</span>}
                    </td>
                    <td className="num">{ot.r2 ? minutesText(ot.r2) : '—'}</td>
                    <td className="num">{ot.r3 ? minutesText(ot.r3) : '—'}</td>
                    <td>{d.workDayRemark || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
